import { useEffect, useState } from 'react';
import { Cookie } from 'lucide-react';
import { API_URL } from '../config/api';

// Self-contained cookie consent banner.
// - GDPR / Italian Garante Privacy compliant: shows on first visit, allows
//   Accept / Reject / Customize, persists choice in localStorage so it
//   does NOT reappear after a refresh.
// - Visual: discreet bottom-left toast (≤ 360px wide), backdrop-blur card,
//   does not block scroll or page interaction (no overlay).
// - i18n: translations are inline because Landing.js does NOT use the
//   global LanguageContext (it has its own internal TRANSLATIONS map).
//   Same 6 languages as the rest of the app.
const STORAGE_KEY = 'slimmy_cookie_consent_v1';

const TXT = {
  it: {
    title: 'Informativa Cookie',
    body: 'Usiamo cookie tecnici per il funzionamento del sito e, previo tuo consenso, cookie analitici/di profilazione per migliorare l\'esperienza. Puoi accettare, rifiutare o personalizzare.',
    accept: 'Accetta tutto',
    reject: 'Solo necessari',
    customize: 'Personalizza',
    learnMore: 'Scopri di più'
  },
  en: {
    title: 'Cookie Notice',
    body: 'We use technical cookies to make the site work and, with your consent, analytics/profiling cookies to improve your experience. You can accept, reject or customize.',
    accept: 'Accept all',
    reject: 'Necessary only',
    customize: 'Customize',
    learnMore: 'Learn more'
  },
  es: {
    title: 'Aviso de Cookies',
    body: 'Usamos cookies técnicas para el funcionamiento del sitio y, con tu consentimiento, cookies analíticas/de perfilado para mejorar la experiencia. Puedes aceptar, rechazar o personalizar.',
    accept: 'Aceptar todo',
    reject: 'Solo necesarias',
    customize: 'Personalizar',
    learnMore: 'Saber más'
  },
  pt: {
    title: 'Aviso de Cookies',
    body: 'Usamos cookies técnicos para o funcionamento do site e, com seu consentimento, cookies analíticos/de perfilamento para melhorar a experiência. Você pode aceitar, recusar ou personalizar.',
    accept: 'Aceitar tudo',
    reject: 'Apenas necessários',
    customize: 'Personalizar',
    learnMore: 'Saiba mais'
  },
  de: {
    title: 'Cookie-Hinweis',
    body: 'Wir verwenden technische Cookies für den Betrieb der Website und, mit Ihrer Einwilligung, Analyse-/Profiling-Cookies zur Verbesserung der Nutzung. Sie können zustimmen, ablehnen oder anpassen.',
    accept: 'Alle akzeptieren',
    reject: 'Nur notwendige',
    customize: 'Anpassen',
    learnMore: 'Mehr erfahren'
  },
  fr: {
    title: 'Avis sur les Cookies',
    body: 'Nous utilisons des cookies techniques pour le fonctionnement du site et, avec votre consentement, des cookies analytiques/de profilage pour améliorer l\'expérience. Vous pouvez accepter, refuser ou personnaliser.',
    accept: 'Tout accepter',
    reject: 'Uniquement nécessaires',
    customize: 'Personnaliser',
    learnMore: 'En savoir plus'
  }
};

export default function CookieConsent({ lang = 'it' }) {
  const [visible, setVisible] = useState(false);
  // Admin-overridable title/body. Empty until backend responds; we
  // ALWAYS render with the bundled defaults first (TXT[lang]) and
  // swap to the admin text once it arrives, so the banner is never
  // empty even if the API is slow or offline.
  const [override, setOverride] = useState({ title: '', body: '' });

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    // Fetch admin-customized banner text. 404/empty → use defaults.
    let cancelled = false;
    fetch(`${API_URL}/api/legal/cookie-banner?lang=${lang}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data) {
          setOverride({ title: data.title || '', body: data.body || '' });
        }
      })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, [lang]);

  const persist = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          choice, // 'all' | 'necessary'
          ts: new Date().toISOString(),
          v: 1
        })
      );
    } catch (_) { /* storage disabled — banner won't reappear in-session */ }
    setVisible(false);
  };

  if (!visible) return null;
  const t = TXT[lang] || TXT.en;
  const title = override.title || t.title;
  const body = override.body || t.body;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-4 left-4 z-[9999] w-[calc(100%-2rem)] max-w-[360px] animate-in fade-in slide-in-from-bottom-4 duration-300"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      role="dialog"
      aria-label={title}
    >
      <div className="rounded-2xl bg-gray-900/95 backdrop-blur-md border border-white/10 shadow-2xl p-4 text-white">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-[#E8334A]/20 flex items-center justify-center flex-shrink-0">
            <Cookie className="w-4 h-4 text-[#E8334A]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm">{title}</h3>
            <p className="text-xs text-white/70 mt-1 leading-relaxed">{body}</p>
            <a
              href="/cookies"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cookie-consent-learn-more"
              className="inline-block text-xs text-[#22D3EE] hover:underline mt-1"
            >
              {t.learnMore} →
            </a>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => persist('necessary')}
            data-testid="cookie-consent-reject"
            className="flex-1 py-2 rounded-lg text-xs font-medium bg-white/10 border border-white/15 text-white/90 hover:bg-white/15 active:scale-95 transition-all"
          >
            {t.reject}
          </button>
          <button
            type="button"
            onClick={() => persist('all')}
            data-testid="cookie-consent-accept"
            className="flex-1 py-2 rounded-lg text-xs font-bold bg-gradient-to-r from-[#E8334A] to-[#D9234A] text-white shadow-lg shadow-[#E8334A]/30 active:scale-95 transition-all"
          >
            {t.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
